import React, { useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { X, ChevronLeft, ChevronRight, Download, Share2 } from 'lucide-react';
import { Button } from '../ui/button';

function Lightbox() {
  const { state, actions } = useApp();
  
  const currentImage = state.lightboxImages[state.lightboxCurrentIndex];

  const navigateLightbox = useCallback((direction) => {
    const total = state.lightboxImages.length;
    if (direction === 'next') {
      actions.setLightboxCurrentIndex((state.lightboxCurrentIndex + 1) % total);
    } else {
      actions.setLightboxCurrentIndex((state.lightboxCurrentIndex - 1 + total) % total);
    }
  }, [state.lightboxImages.length, state.lightboxCurrentIndex, actions]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          actions.setShowLightbox(false);
          break;
        case 'ArrowLeft':
          navigateLightbox('prev');
          break;
        case 'ArrowRight':
          navigateLightbox('next');
          break;
        default:
          break;
      }
    };

    if (state.showLightbox) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [state.showLightbox, actions, navigateLightbox]);

  const handleClose = () => {
    actions.setShowLightbox(false);
  };

  const handleBackdropClick = (e) => {
    // Click on backdrop (outside the image) should close
    if (e.target === e.currentTarget) {
      actions.setShowLightbox(false);
    }
  };

  if (!state.showLightbox || !currentImage) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClose}
        className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20"
        style={{ zIndex: 100000 }}
      >
        <X size={24} />
      </Button>

      {/* Navigation Buttons */}
      {state.lightboxImages.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateLightbox('prev')}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20"
            style={{ zIndex: 100000 }}
          >
            <ChevronLeft size={32} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateLightbox('next')}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20"
            style={{ zIndex: 100000 }}
          >
            <ChevronRight size={32} />
          </Button>
        </>
      )}

      {/* Image */}
      <div className="max-w-[90vw] max-h-[90vh] flex flex-col">
        <img
          src={currentImage.url}
          alt={currentImage.title}
          className="max-w-full max-h-[80vh] object-contain"
        />
        
        {/* Image Info */}
        {(currentImage.title || currentImage.description) && (
          <div className="mt-4 text-white text-center max-w-2xl">
            {currentImage.title && (
              <h3 className="text-lg font-semibold mb-2">{currentImage.title}</h3>
            )}
            {currentImage.description && (
              <p className="text-sm text-gray-300">{currentImage.description}</p>
            )}
          </div>
        )}
      </div>

      {/* Image Counter */}
      {state.lightboxImages.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
          {state.lightboxCurrentIndex + 1} / {state.lightboxImages.length}
        </div>
      )}

      {/* Action Buttons */}
      <div className="absolute bottom-4 right-4 flex space-x-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const link = document.createElement('a');
            link.href = currentImage.url;
            link.download = currentImage.title || 'image';
            link.click();
          }}
          className="text-white hover:bg-white hover:bg-opacity-20"
        >
          <Download size={16} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigator.share({
              title: currentImage.title,
              text: currentImage.description,
              url: currentImage.url
            }).catch(() => {
              // Fallback to clipboard
              navigator.clipboard.writeText(currentImage.url);
              alert('Lien de l\'image copié !');
            });
          }}
          className="text-white hover:bg-white hover:bg-opacity-20"
        >
          <Share2 size={16} />
        </Button>
      </div>
    </div>
  );
}

export default Lightbox;